import { NotFoundApiError } from './common/api/exceptions'
import { DISPLAY_STATUSES, WIDGET_MODES } from './common/constants'
import { ENVNAME, SCRIPT_URL, WIDGET_URL } from './constants/environment'
import { ExpectedError } from './exceptions'
import buildIntl from './intl'
import initializePreviewWidget from './previewMain'
import { createSession } from './services/customerSessions'
import { fetchWidget } from './services/widgets'
import styles from './styles.scss?inline'
import widgetMarkup from './templates/widget.html?raw'
import buildElement from './utils/buildElement'
import cacheRequest, { clearExpiredCaches } from './utils/cacheRequest'
import { catchActionError, catchAsyncActionError } from './utils/catchErrors'
import IframeMessage from './utils/IframeMessage'
import WidgetState from './utils/widgetState'

const main = async () => {
  try {
    clearExpiredCaches()

    let host: HTMLSpanElement | undefined = undefined
    let container: HTMLDivElement | undefined = undefined
    const scriptElement =
      document.querySelector<HTMLScriptElement>(`script[src^="${SCRIPT_URL}"]`) ||
      document.querySelector<HTMLScriptElement>(`#ccn-widget`)
    if (!scriptElement) {
      throw new Error('Missing: Script Element')
    }

    const dataAppId =
      scriptElement.dataset.appId ||
      new URL(scriptElement.src).searchParams.get('appId') ||
      undefined
    const { displayStatus, parentSelector: inParentSelector, mode: inMode } = scriptElement.dataset

    const mode = Object.values(WIDGET_MODES).find(item => item === inMode) || WIDGET_MODES.Widget

    const parentElement = inParentSelector
      ? document.querySelector(inParentSelector)
      : document.body

    if (!parentElement) {
      throw new Error('Not Found Parent Element')
    }

    if (mode === WIDGET_MODES.Preview) {
      await initializePreviewWidget(scriptElement, parentElement)
      return
    }

    if (!dataAppId) {
      throw new Error('Missing: App Id param')
    }

    const destroy = () => {
      scriptElement.removeEventListener('destroy', destroy)
      scriptElement.removeEventListener('reload', handleReload)
      if (host) {
        parentElement.removeChild(host)
      }
    }

    const handleDestroy = catchActionError(destroy)

    const handleReload = catchAsyncActionError(async () => {
      destroy()
      await main()
    })

    scriptElement.addEventListener('reload', handleReload)
    scriptElement.addEventListener('destroy', handleDestroy)

    const widget = await (async () => {
      try {
        return await cacheRequest(fetchWidget, [dataAppId, mode], 'fetchWidget')
      } catch (error) {
        if (error instanceof NotFoundApiError) {
          throw new ExpectedError('Not Found: App Id')
        }
        throw error
      }
    })()

    const iframeSrc =
      widget.attributes.mode === WIDGET_MODES.Playground ||
      widget.attributes.mode === WIDGET_MODES.Widget
        ? `${WIDGET_URL}/chat`
        : undefined

    if (!iframeSrc) throw new Error('Unresolved iframe source')

    const intl = await buildIntl(widget.attributes.locale)

    const containerMarkup =
      widget.attributes.mode === WIDGET_MODES.Playground
        ? (await import('./templates/playground.html?raw')).default
        : widgetMarkup

    host = document.createElement('span')
    host.id = `ccn-widget:${dataAppId}`

    container = buildElement<HTMLDivElement>(
      containerMarkup
        .replaceAll('{mode}', widget.attributes.mode)
        .replaceAll('{SCRIPT_URL}', SCRIPT_URL)
        .replaceAll('{title}', widget.attributes.title || intl.get('title'))
        .replaceAll('{title_icon}', widget.attributes.title_icon || `${SCRIPT_URL}/ai-avatar.svg`)
        .replaceAll('{rules}', styles),
    )

    const shadowRoot = host.attachShadow({ mode: 'open' })
    shadowRoot.appendChild(container)

    if (widget.attributes.primary_color) {
      container.style.setProperty('--primary', widget.attributes.primary_color)
    }

    const mainElement = container.querySelector<HTMLDivElement>('.ccn-widget__main')
    const iframe = container.querySelector<HTMLIFrameElement>('iframe')

    if (!mainElement || !iframe) {
      throw new Error('Missing Elements: Main, Iframe')
    }

    const iframeMessaging = new IframeMessage(iframe)

    iframeMessaging.postWidget(widget)
    iframeMessaging.postEcommerceReferer(window.location.href)

    const widgetState = new WidgetState(dataAppId, {
      displayStatus:
        widget.attributes.mode === WIDGET_MODES.Playground
          ? DISPLAY_STATUSES.Open
          : Object.values(DISPLAY_STATUSES).find(s => s === displayStatus),
    })

    const auth = async () => {
      const session = await createSession(dataAppId, widget.attributes.mode)
      iframeMessaging.postAuthToken(session.attributes.auth_token)
    }

    const mountApp = async () => {
      if (!iframe.src.startsWith(iframeSrc)) {
        await auth()
        iframe.src = iframeSrc
      }
    }

    const open = async (avoidAnimation?: boolean) => {
      await mountApp()
      const changeContainerClasses = () => {
        container.classList.remove('ccn-widget--maximized')
        container.classList.remove('ccn-widget--closed')
        container.classList.add('ccn-widget--open')
      }

      if (!avoidAnimation) {
        mainElement?.classList.add('ccn-widget__main--transition')
        window.setTimeout(catchActionError(changeContainerClasses))
      } else {
        changeContainerClasses()
      }
    }

    const close = (avoidAnimation?: boolean) => {
      if (!avoidAnimation) {
        mainElement?.classList.add('ccn-widget__main--transition')
      }
      container.classList.remove('ccn-widget--maximized')
      container.classList.remove('ccn-widget--open')
      container.classList.add('ccn-widget--closed')
    }

    const maximize = () => {
      container.classList.add('ccn-widget--maximized')
    }

    const unreadDot = container.querySelector('.ccn-widget__actions__toggle-open__dot')

    const showUnreadDot = () => {
      unreadDot?.classList.add('ccn-widget__actions__toggle-open__dot--visible')
    }

    const hideUnreadDot = () => {
      unreadDot?.classList.remove('ccn-widget__actions__toggle-open__dot--visible')
    }

    if (widgetState.chatStatus === 'unread') {
      showUnreadDot()
    }

    if (widgetState.displayStatus === 'closed') {
      close(true)
      if (widgetState.chatStatus === 'responding') {
        await mountApp()
      }
    }
    if (widgetState.displayStatus === 'open') {
      await open(true)
    }
    if (widgetState.displayStatus === 'maximized') {
      await open(true)
      maximize()
    }

    const isOpen = () => {
      return container.classList.contains('ccn-widget--open')
    }

    const toggleOpen = async () => {
      if (isOpen()) {
        close()
        iframeMessaging.postDisplayStatus('closed')
        widgetState.setDisplayStatus('closed')
      } else {
        toggleOpenButtons.forEach(button => button && (button.disabled = true))
        await open()
        toggleOpenButtons.forEach(button => button && (button.disabled = false))
        iframeMessaging.postDisplayStatus('open')
        widgetState.setDisplayStatus('open')
      }
    }

    const isMaximized = () => {
      return container.classList.contains('ccn-widget--maximized')
    }

    const toggleMaximize = async () => {
      if (isMaximized()) {
        await open()
        iframeMessaging.postDisplayStatus('open')
        widgetState.setDisplayStatus('open')
      } else {
        maximize()
        iframeMessaging.postDisplayStatus('maximized')
        widgetState.setDisplayStatus('maximized')
      }
    }

    container.addEventListener(
      'transitionend',
      catchActionError(e => {
        if (e.target === mainElement) {
          mainElement?.classList.remove('ccn-widget__main--transition')
        }
      }),
    )

    const toggleOpenButtons = [
      container.querySelector<HTMLButtonElement>('.ccn-widget__main__header__closeButton'),
      container.querySelector<HTMLButtonElement>('.ccn-widget__actions__toggle-open'),
    ]
    toggleOpenButtons.forEach(button => {
      button?.addEventListener('click', catchAsyncActionError(toggleOpen))
    })

    container
      .querySelector<HTMLButtonElement>('.ccn-widget__main__header__maximizeButton')
      ?.addEventListener('click', catchAsyncActionError(toggleMaximize))

    iframeMessaging.onSetChatStatus(
      catchActionError(value => {
        widgetState.setChatStatus(value)

        hideUnreadDot()
        if (value === 'unread') {
          showUnreadDot()
        }
      }),
    )

    parentElement.appendChild(host)
  } catch (error) {
    if (error instanceof ExpectedError) {
      if (ENVNAME === 'local') {
        console.error(`[ccn-widget] ${error.message}`)
      }
      return
    } else {
      console.error('[ccn-widget] Failed to load widget')
    }
    throw error
  }
}

export default main
